import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import analyticsTracker from '../utils/analyticsTracker';
import { askStewieStructured } from '../services/askStewieClient';
import { ArrowLeft, Send, MessageSquare, User, Plus, Clock, Trash2, PanelLeftClose, PanelLeft } from 'lucide-react';
import { StewieLogo } from '../components/StewieLogo';
import StructuredAnswerCard from '../components/StructuredAnswerCard';
import AskStewieAnswerHeader from '../components/askstewie/AskStewieAnswerHeader';
import MultiPathPanels from '../components/answer/multiPathPanels';
import VerifiedAnswerCard from '../components/askstewie/VerifiedAnswerCard';
import ActionCenter from '../components/askstewie/ActionCenter';
import ExamplePrompts from '../components/ExamplePrompts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Map AskStewie's `primary_issue` slug to the Case Locker IssueType enum
 * accepted by `POST /api/case-locker/cases`.
 */
const ISSUE_MAP = {
  overtime_bypass:      'overtime_dispute',
  overtime:             'overtime_dispute',
  mandatory_overtime:   'overtime_dispute',
  discipline:           'discipline',
  discharge:            'discipline',
  termination:          'discipline',
  weingarten:           'weingarten_violation',
  weingarten_violation: 'weingarten_violation',
  seniority:            'seniority_dispute',
  seniority_dispute:    'seniority_dispute',
  contracting:          'contracting',
  contracting_out:      'contracting',
  safety:               'safety_violation',
  safety_violation:     'safety_violation',
  injury_near_miss:     'injury_near_miss',
  harassment:           'harassment_conduct',
  harassment_conduct:   'harassment_conduct',
  pay_wage_issue:       'pay_wage_issue',
  benefits_pay:         'pay_wage_issue',
  scheduling:           'scheduling',
  drug_alcohol:         'discipline',
};

function pickIssueType(answer) {
  const slug = String(answer?.primary_issue || answer?.detected_issues?.[0] || '').toLowerCase();
  return ISSUE_MAP[slug] || 'other';
}

/**
 * Persist an Ask Stewie Q/A as a new Case Locker case, then navigate to it.
 * Falls back to /case-locker on failure so the action never feels broken.
 */
async function saveAskStewieToCase({ question, answer }) {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
    const today = new Date().toISOString().slice(0, 10);
    const verdict = answer?.explanation || answer?.kid_explanation || answer?.what_went_wrong || '';
    const title = (question || 'Saved from Ask Stewie').slice(0, 120);
    const summary = [
      verdict ? verdict.slice(0, 600) : '',
      answer?.key_takeaway ? `\n\nKey takeaway: ${answer.key_takeaway}` : '',
    ].join('').trim() || `Saved from Ask Stewie on ${today}.`;

    const res = await axios.post(
      `${API}/api/case-locker/cases`,
      {
        title,
        issue_type: pickIssueType(answer),
        incident_date: today,
        summary,
        notes: `Question asked: "${question || ''}"`,
      },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    const caseId = res?.data?.case?.case_id || res?.data?.case?.id;
    if (caseId) {
      window.location.href = `/case-locker/${caseId}`;
      return;
    }
  } catch (e) {
    console.warn('saveAskStewieToCase failed:', e?.response?.status, e?.message);
  }
  window.location.href = '/case-locker';
}


const TRADES = [
  { value: "", label: "Any / All" },
  { value: "journeyman", label: "Journeyman" },
  { value: "apprentice", label: "Apprentice" },
  { value: "lineman", label: "Lineman" },
  { value: "electrician", label: "Electrician" },
  { value: "cable_splicer", label: "Cable Splicer" },
  { value: "mechanic", label: "Mechanic" },
  { value: "operator", label: "Operator" },
  { value: "foreman", label: "Foreman" },
  { value: "locator", label: "Locator" },
  { value: "clerical", label: "Clerical" },
  { value: "substation", label: "Substation Tech" },
];

export default function AskStewieAnalytics() {
  const [question, setQuestion] = useState('');
  const [structuredAnswer, setStructuredAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trade, setTrade] = useState('');
  const [showContext, setShowContext] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRef = useRef(null);

  // Conversation memory state
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [history, setHistory] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  // Track the most recently submitted question so the new Answer Header can show it
  const [lastQuestion, setLastQuestion] = useState('');

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    analyticsTracker.trackPageView('ask_stewie');
    try {
      const saved = JSON.parse(localStorage.getItem('stewie_user_context') || '{}');
      if (saved.trade) setTrade(saved.trade);
    } catch {}
    const q = searchParams.get('q');
    if (q) {
      setQuestion(q);
      setTimeout(() => handleSubmit(null, q), 200);
    }
  }, []);

  useEffect(() => {
    if (trade) {
      localStorage.setItem('stewie_user_context', JSON.stringify({ trade }));
    }
  }, [trade]);

  async function loadSessions() {
    try {
      const res = await axios.get(`${API}/api/ask-stewie/memory/sessions`, { headers });
      setSessions(res.data.sessions || []);
    } catch { /* silent */ }
  }

  async function startNewSession() {
    try {
      const res = await axios.post(`${API}/api/ask-stewie/memory/sessions`, {}, { headers });
      const newId = res.data.session_id;
      setSessionId(newId);
      setHistory([]);
      setStructuredAnswer(null);
      setQuestion('');
      loadSessions();
      return newId;
    } catch { return null; }
  }

  async function loadSession(sid) {
    try {
      const res = await axios.get(`${API}/api/ask-stewie/memory/sessions/${sid}`, { headers });
      setSessionId(sid);
      setHistory(res.data.messages || []);
      setStructuredAnswer(null);
      setShowSessions(false);
    } catch { /* silent */ }
  }

  async function deleteSession(sid, e) {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/ask-stewie/memory/sessions/${sid}`, { headers });
      if (sessionId === sid) {
        setSessionId(null);
        setHistory([]);
        setStructuredAnswer(null);
      }
      loadSessions();
    } catch { /* silent */ }
  }

  async function handleSubmit(e, overrideQ) {
    if (e) e.preventDefault();
    const q = overrideQ || question;
    if (!q.trim()) return;
    setLoading(true);
    setError('');
    setStructuredAnswer(null);
    setLastQuestion(q);
    analyticsTracker.trackQuestionAsked(q, 'ask_stewie');

    const userContext = trade ? { trade } : undefined;

    // Auto-create session if none active
    let sid = sessionId;
    if (!sid) {
      sid = await startNewSession();
    }

    try {
      const data = await askStewieStructured(q, { token, userContext, sessionId: sid });
      setStructuredAnswer(data.answer);
      analyticsTracker.trackAnswerGenerated(q);
      // Add to local history
      setHistory(prev => [...prev, {
        question: q,
        question_issue: data.answer?.question_issue || '',
        answer_mode: data.answer?.mode || '',
        short_answer: (data.answer?.short_answer || '').slice(0, 200),
        timestamp: new Date().toISOString(),
      }]);
      loadSessions();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to get answer. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="stewie-page" data-testid="ask-stewie-page">
      <style>{stewieStyles}</style>

      {/* Header — back button and logo provided by AppHeader */}
      <div className="stewie-header">
        <div className="stewie-header-title">
          <span>Ask Stewie</span>
        </div>
        <button
          className="stewie-session-toggle"
          onClick={() => setShowSessions(!showSessions)}
          data-testid="sessions-toggle"
          title="Conversation history"
        >
          {showSessions ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
          <span className="stewie-session-count">{sessions.length}</span>
        </button>
        <button
          className={`stewie-context-toggle ${showContext ? 'active' : ''}`}
          onClick={() => setShowContext(!showContext)}
          data-testid="context-toggle"
          title="Set your trade/classification"
        >
          <User size={16} />
          {trade && <span className="stewie-trade-badge">{trade}</span>}
        </button>
      </div>

      {/* Sessions panel */}
      {showSessions && (
        <div className="stewie-sessions-panel" data-testid="sessions-panel">
          <div className="stewie-sessions-header">
            <span className="stewie-sessions-title">Conversations</span>
            <button className="stewie-new-session-btn" onClick={startNewSession} data-testid="new-session-btn">
              <Plus size={14} /> New
            </button>
          </div>
          {sessions.length === 0 ? (
            <div className="stewie-sessions-empty">No conversations yet. Ask a question to start.</div>
          ) : (
            <div className="stewie-sessions-list">
              {sessions.map(s => (
                <div
                  key={s.session_id}
                  className={`stewie-session-item ${sessionId === s.session_id ? 'active' : ''}`}
                  onClick={() => loadSession(s.session_id)}
                  data-testid={`session-${s.session_id}`}
                >
                  <div className="stewie-session-info">
                    <span className="stewie-session-preview">{s.preview}</span>
                    <span className="stewie-session-meta">{s.message_count || 0} turns</span>
                  </div>
                  <button
                    className="stewie-session-delete"
                    onClick={(e) => deleteSession(s.session_id, e)}
                    data-testid={`delete-session-${s.session_id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Session history (previous turns in current session) */}
      {history.length > 0 && (
        <div className="stewie-history" data-testid="session-history">
          <div className="stewie-history-label">
            <Clock size={12} /> {history.length} previous {history.length === 1 ? 'turn' : 'turns'} in this session
          </div>
          {history.map((h, i) => (
            <div key={i} className="stewie-history-turn" data-testid={`history-turn-${i}`}>
              <span className="stewie-history-q">{h.question}</span>
              <span className="stewie-history-issue">{h.question_issue}</span>
            </div>
          ))}
        </div>
      )}

      {/* User Context Panel */}
      {showContext && (
        <div className="stewie-context-panel" data-testid="context-panel">
          <div className="stewie-context-label">Your Classification</div>
          <div className="stewie-context-hint">Setting this improves answer accuracy for your trade.</div>
          <div className="stewie-context-options">
            {TRADES.map(t => (
              <button
                key={t.value}
                className={`stewie-trade-opt ${trade === t.value ? 'selected' : ''}`}
                onClick={() => { setTrade(t.value); setShowContext(false); }}
                data-testid={`trade-${t.value || 'any'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Example Prompts — shown when no answer and not loading */}
      {!structuredAnswer && !loading && (
        <ExamplePrompts onPick={(q) => { setQuestion(q); handleSubmit(null, q); }} />
      )}

      {/* Search */}
      <form className="stewie-search" onSubmit={handleSubmit}>
        <div className="stewie-input-wrap">
          <MessageSquare size={18} className="stewie-input-icon" />
          <textarea
            ref={inputRef}
            data-testid="question-input"
            className="stewie-textarea"
            placeholder="Ask about your union contract..."
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
            rows={2}
          />
        </div>
        <button type="submit" className="stewie-ask-btn" disabled={loading || !question.trim()} data-testid="ask-btn">
          {loading ? <span className="stewie-spinner" /> : <Send size={18} />}
        </button>
      </form>

      {error && <div className="stewie-error" data-testid="error-message">{error}</div>}

      {loading && (
        <div className="stewie-loading" data-testid="loading-indicator">
          <StewieLogo size={24} />
          <span>Stewie is thinking...</span>
        </div>
      )}

      {/* Structured Answer — the only answer mode */}
      {structuredAnswer && !loading && (
        <>
          <VerifiedAnswerCard
            question={structuredAnswer.question_asked || lastQuestion}
            answer={structuredAnswer}
          />

          <ActionCenter
            question={structuredAnswer.question_asked || lastQuestion}
            answer={structuredAnswer}
            onFocusInput={() => {
              const el = inputRef.current;
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.focus();
              }
            }}
          />

          {/* P92: Multi-Path Contract Analysis Engine — always-visible panels.
              Renders Direct Answer, Where AskStewie Looked, Related Contract
              Language, Contract vs Policy, and admin-only Reasoning Path. */}
          <MultiPathPanels answer={structuredAnswer} />

          {/* Legacy Steward Toolkit — collapsed by default so the Verified
              Answer card gets 100% of first-viewport attention. */}
          <details className="asa-toolkit-toggle" data-testid="steward-toolkit-toggle">
            <summary className="asa-toolkit-summary">
              <span className="asa-toolkit-eyebrow">STEWARD TOOLKIT</span>
              <span className="asa-toolkit-hint">
                Grievance draft · Evidence checklist · Deadlines · Sources · Actions
              </span>
              <span className="asa-toolkit-caret" aria-hidden="true">▾</span>
            </summary>
            <div className="asa-toolkit-body">
              <AskStewieAnswerHeader
                question={structuredAnswer.question_asked || lastQuestion}
                answer={structuredAnswer}
                onSaveToCase={async () => {
                  await saveAskStewieToCase({
                    question: structuredAnswer.question_asked || lastQuestion,
                    answer: structuredAnswer,
                  });
                }}
              />
          <StructuredAnswerCard
            answer={structuredAnswer}
          onFollowUpClick={(q, pillIndex, pillId) => {
            // 1) Fire-and-forget click tracking (never blocks or delays UI)
            try {
              const caseId =
                structuredAnswer?.case_id ||
                searchParams.get('case_id') ||
                undefined;
              axios.post(
                `${API}/api/ask-stewie/pill-clicked`,
                {
                  pill_text: q,
                  pill_index: typeof pillIndex === 'number' ? pillIndex : 0,
                  pill_id: pillId || null,
                  issue_type:
                    structuredAnswer?.question_issue ||
                    structuredAnswer?.primary_issue ||
                    null,
                  response_mode:
                    structuredAnswer?.response_mode ||
                    structuredAnswer?.mode ||
                    null,
                  case_id: caseId || null,
                },
                { timeout: 4000 },
              ).catch(() => { /* swallow - analytics must never disrupt UX */ });
            } catch (_) { /* noop */ }

            // 2) Populate the input (existing behavior - no auto-submit)
            setQuestion(q);
            if (inputRef.current) {
              inputRef.current.focus();
              try {
                const end = q.length;
                inputRef.current.setSelectionRange(end, end);
              } catch (_) { /* noop */ }
            }
            if (typeof window !== 'undefined') {
              const el = inputRef.current;
              if (el && el.scrollIntoView) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }
          }}
        />
            </div>
          </details>
        </>
      )}
    </div>
  );
}

const stewieStyles = `
.stewie-page {
  max-width: 780px;
  margin: 0 auto;
  padding: 20px 16px;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, sans-serif;
  background: var(--surface-base);
  color: var(--text-primary);
}

.stewie-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.stewie-back-btn {
  background: none;
  border: 1px solid var(--border-default);
  color: var(--text-secondary);
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
}
.stewie-back-btn:hover { border-color: var(--border-hover); color: var(--text-primary); }
.stewie-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  flex: 1;
}

/* Context toggle */
.stewie-context-toggle {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 8px; cursor: pointer;
  background: none; border: 1px solid var(--border-default);
  color: var(--text-secondary); font-size: 12px; font-weight: 600;
  transition: all 0.15s;
}
.stewie-context-toggle:hover { border-color: var(--border-hover); color: var(--text-primary); }
.stewie-context-toggle.active { border-color: rgba(6,182,212,0.4); color: #67e8f9; background: rgba(6,182,212,0.06); }
.stewie-trade-badge {
  padding: 1px 8px; border-radius: 99px; font-size: 10px; font-weight: 700;
  background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.2); color: #34d399;
  text-transform: uppercase;
}

/* Context panel */
.stewie-context-panel {
  background: rgba(24,24,27,0.8); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px; padding: 16px; margin-bottom: 14px;
}
.stewie-context-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #a1a1aa; margin-bottom: 4px; }
.stewie-context-hint { font-size: 12px; color: #71717a; margin-bottom: 12px; }
.stewie-context-options { display: flex; flex-wrap: wrap; gap: 6px; }
.stewie-trade-opt {
  padding: 6px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.15s;
  background: rgba(39,39,42,0.6); border: 1px solid rgba(255,255,255,0.06);
  color: #a1a1aa;
}
.stewie-trade-opt:hover { border-color: rgba(255,255,255,0.15); color: #e4e4e7; }
.stewie-trade-opt.selected { background: rgba(16,185,129,0.1); border-color: rgba(16,185,129,0.3); color: #34d399; }

/* Search */
.stewie-search {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  align-items: flex-end;
  margin-top: 16px;
}
.stewie-input-wrap {
  flex: 1;
  position: relative;
}
.stewie-input-icon {
  position: absolute;
  left: 14px;
  top: 14px;
  color: var(--text-muted);
  pointer-events: none;
}
.stewie-textarea {
  width: 100%;
  padding: 12px 14px 12px 42px;
  background: var(--surface-input);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  color: var(--text-primary);
  font-size: 14px;
  resize: none;
  outline: none;
  font-family: inherit;
  line-height: 1.5;
  box-sizing: border-box;
}
.stewie-textarea:focus { border-color: var(--color-accent); box-shadow: 0 0 0 3px var(--color-accent-soft); }
.stewie-ask-btn {
  background: var(--color-accent);
  border: none;
  color: var(--text-inverse);
  width: 44px;
  height: 44px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s;
}
.stewie-ask-btn:hover:not(:disabled) { transform: scale(1.05); }
.stewie-ask-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.stewie-spinner {
  width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,0.25);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Error / Loading */
.stewie-error {
  background: var(--color-danger-soft);
  border: 1px solid rgba(239, 68, 68, 0.25);
  color: #fca5a5;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 12px;
}
.stewie-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  color: var(--text-secondary);
  font-size: 14px;
}

@media (max-width: 640px) {
  .stewie-page { padding: 12px 10px; }
  .stewie-header-title { font-size: 16px; }
  .stewie-context-options { gap: 4px; }
  .stewie-trade-opt { padding: 5px 10px; font-size: 11px; }
}

/* Session toggle button */
.stewie-session-toggle {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 10px; border-radius: 8px; cursor: pointer;
  background: none; border: 1px solid var(--border-default);
  color: var(--text-secondary); font-size: 12px; font-weight: 600;
  transition: all 0.15s;
}
.stewie-session-toggle:hover { border-color: var(--border-hover); color: var(--text-primary); }
.stewie-session-count {
  padding: 0 5px; font-size: 10px; font-weight: 700;
  background: rgba(168,85,247,0.15); color: #c084fc;
  border-radius: 99px; min-width: 16px; text-align: center;
}

/* Sessions panel */
.stewie-sessions-panel {
  background: rgba(24,24,27,0.85); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px; padding: 12px; margin-bottom: 14px;
}
.stewie-sessions-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}
.stewie-sessions-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #a1a1aa; }
.stewie-new-session-btn {
  display: flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600;
  cursor: pointer; border: 1px solid rgba(168,85,247,0.3);
  background: rgba(168,85,247,0.08); color: #c084fc; transition: all 0.15s;
}
.stewie-new-session-btn:hover { background: rgba(168,85,247,0.15); }
.stewie-sessions-empty { font-size: 12px; color: #71717a; padding: 8px 0; }
.stewie-sessions-list { display: flex; flex-direction: column; gap: 4px; max-height: 220px; overflow-y: auto; }
.stewie-session-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 10px; border-radius: 8px; cursor: pointer;
  background: rgba(39,39,42,0.5); border: 1px solid transparent;
  transition: all 0.15s;
}
.stewie-session-item:hover { border-color: rgba(255,255,255,0.08); background: rgba(39,39,42,0.8); }
.stewie-session-item.active { border-color: rgba(168,85,247,0.3); background: rgba(168,85,247,0.06); }
.stewie-session-info { flex: 1; min-width: 0; }
.stewie-session-preview { font-size: 12px; color: #d4d4d8; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stewie-session-meta { font-size: 10px; color: #71717a; }
.stewie-session-delete {
  background: none; border: none; color: #71717a; cursor: pointer;
  padding: 4px; border-radius: 4px; display: flex; transition: all 0.15s;
}
.stewie-session-delete:hover { color: #f87171; background: rgba(239,68,68,0.1); }

/* Session history (previous turns) */
.stewie-history {
  background: rgba(24,24,27,0.5); border: 1px solid rgba(255,255,255,0.04);
  border-radius: 10px; padding: 10px; margin-bottom: 12px;
}
.stewie-history-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600; color: #71717a;
  text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;
}
.stewie-history-turn {
  display: flex; align-items: center; gap: 8px;
  padding: 4px 0; font-size: 12px;
}
.stewie-history-q {
  flex: 1; color: #a1a1aa; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap;
}
.stewie-history-issue {
  font-size: 10px; font-weight: 600; padding: 1px 6px;
  border-radius: 4px; background: rgba(168,85,247,0.1);
  color: #c084fc; white-space: nowrap;
}

/* ── P86.2 Steward Toolkit toggle (collapsed by default) ───────────── */
.asa-toolkit-toggle {
  margin-top: 8px;
  background: rgba(15, 27, 70, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  overflow: hidden;
}
.asa-toolkit-toggle[open] {
  border-color: rgba(34, 211, 238, 0.35);
}
.asa-toolkit-summary {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  list-style: none;
  color: #cbd5e1;
  user-select: none;
  transition: background 0.15s;
}
.asa-toolkit-summary::-webkit-details-marker { display: none; }
.asa-toolkit-summary:hover { background: rgba(255, 255, 255, 0.02); }
.asa-toolkit-eyebrow {
  font-size: 11px; font-weight: 800; letter-spacing: 0.18em;
  color: #facc15; text-transform: uppercase;
  flex-shrink: 0;
}
.asa-toolkit-hint {
  flex: 1; min-width: 0;
  font-size: 12px; color: #94a3b8;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.asa-toolkit-caret {
  color: #64748b; font-size: 16px; font-weight: 700;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  flex-shrink: 0;
}
.asa-toolkit-toggle[open] .asa-toolkit-caret { transform: rotate(180deg); color: #22d3ee; }
.asa-toolkit-body {
  padding: 14px 14px 16px;
  border-top: 1px dashed rgba(148, 163, 184, 0.14);
}
`;
